import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, removeStoredToken, storeToken } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Search, Package, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useStaffIdleTimeout } from "@/lib/staffIdle";

/**
 * Staff catalog — full catalog including hidden-by-stock wines, with numeric
 * stock and article codes. Search runs SERVER-SIDE against `/api/admin/wines`
 * (staff role also allowed) with a 250 ms debounce, so waiters can find any
 * bottle among 1900+ imports by name, producer, denomination, uvaggio or SKU.
 */
export default function StaffCatalog() {
    const nav = useNavigate();
    const [me, setMe] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const debounceRef = useRef(null);
    const reqIdRef = useRef(0);
    const timeoutMin = Number(sessionStorage.getItem("staff_timeout_min") || 3);

    const refresh = async () => {
        try {
            const { data } = await api.post("/auth/staff/refresh");
            storeToken(data.access_token);
        } catch { /* silent — the guard will kick in on next call */ }
    };
    const warning = useStaffIdleTimeout(timeoutMin, refresh);

    // Bootstrap: verify role and load first page
    useEffect(() => {
        (async () => {
            try {
                const meData = await api.get("/auth/me").then(r => r.data);
                if (meData.role !== "staff" && meData.role !== "admin") {
                    toast.error("Accesso staff richiesto");
                    nav("/staff/login", { replace: true });
                    return;
                }
                setMe(meData);
            } catch (err) {
                if (err.response?.status === 401) {
                    removeStoredToken();
                    nav("/staff/login", { replace: true });
                } else {
                    toast.error("Errore caricamento");
                }
            }
        })();
    }, [nav]);

    // Server-side search with debounce. Runs on every `q` change once `me` loaded.
    useEffect(() => {
        if (!me) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            const reqId = ++reqIdRef.current;
            setLoading(true);
            try {
                const url = q.trim()
                    ? `/admin/wines?q=${encodeURIComponent(q.trim())}&limit=100`
                    : "/admin/wines?limit=100";
                const { data } = await api.get(url);
                if (reqId !== reqIdRef.current) return;      // stale
                setItems(data.items || []);
                setTotal(data.total || 0);
            } catch (err) {
                if (err.response?.status === 401) {
                    removeStoredToken();
                    nav("/staff/login", { replace: true });
                } else {
                    toast.error("Errore caricamento");
                }
            } finally {
                if (reqId === reqIdRef.current) setLoading(false);
            }
        }, q ? 250 : 0);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [q, me, nav]);

    const logout = () => {
        removeStoredToken();
        sessionStorage.removeItem("staff_timeout_min");
        nav("/", { replace: true });
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="staff-catalog-page">
            <header className="bg-white border-b border-[hsl(var(--border))] px-5 py-3 flex items-center justify-between sticky top-0 z-10">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Orterie · Staff</div>
                    <div className="text-sm">{me?.email}</div>
                </div>
                <button onClick={logout} className="co-btn-ghost text-sm" data-testid="staff-logout-btn">
                    <LogOut className="w-4 h-4" /> Esci
                </button>
            </header>

            {warning && (
                <div className="bg-yellow-50 border-b border-yellow-300 px-5 py-2 text-sm text-yellow-900 flex items-center gap-2" data-testid="staff-idle-warning">
                    <AlertTriangle className="w-4 h-4" strokeWidth={1.5} />
                    Sessione in scadenza tra 15 secondi. Interagisci per prolungare.
                </div>
            )}

            <div className="p-5 max-w-4xl mx-auto">
                <div className="mb-4 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Cerca vino, produttore, denominazione, codice articolo…"
                        className="pl-10"
                        data-testid="staff-search-input"
                    />
                    {q && (
                        <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]" data-testid="staff-search-total">
                            {loading ? "Ricerca…" : `${total} risultato/i`}
                        </div>
                    )}
                </div>

                {loading && items.length === 0 && <div className="text-sm">Caricamento…</div>}
                {!loading && items.length === 0 && (
                    <div className="italic text-[hsl(var(--muted-foreground))]" data-testid="staff-no-results">Nessun risultato.</div>
                )}
                <div className="space-y-3">
                    {items.map((w) => (
                        <div key={w.id} className="bg-white border border-[hsl(var(--border))] rounded-sm p-4" data-testid={`staff-wine-${w.id}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium truncate">{w.name} {w.vintage ? <span className="text-[hsl(var(--muted-foreground))]">· {w.vintage}</span> : null}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{w.producer} · {w.denomination || w.region}</div>
                                </div>
                                <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{w.type}</div>
                            </div>
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                                {(w.formats || []).map((f, i) => {
                                    const zero = f.stock_quantity === 0;
                                    // Sopprimi "€ 0,00" anche nella vista staff: se il prezzo
                                    // effettivo (override incluso) è nullo o 0, mostra "—".
                                    const total = f.price ?? f.base_price_public;
                                    const hasPrice = typeof total === "number" && total > 0;
                                    return (
                                        <div key={i} className={`rounded-sm p-2 border ${zero ? "border-[hsl(var(--destructive))] bg-red-50" : "border-[hsl(var(--border))]"}`} data-testid={`staff-format-${w.id}-${i}`}>
                                            <div className="flex items-center gap-1 text-xs">
                                                <Package className="w-3 h-3" strokeWidth={1.5} />
                                                <span>{f.size} L</span>
                                                <span className="ml-auto font-mono">
                                                    {zero ? "ESAURITO" : (f.stock_quantity ?? "n/d") + " pz"}
                                                </span>
                                            </div>
                                            {f.mexal_sku && (
                                                <div className="mt-1 text-[10px] font-mono text-[hsl(var(--muted-foreground))]" data-testid={`staff-sku-${w.id}-${i}`}>
                                                    Cod. {f.mexal_sku}
                                                </div>
                                            )}
                                            <div className="text-base mt-1" data-testid={`staff-price-${w.id}-${i}`}>
                                                {hasPrice ? `€ ${total.toFixed(2)}` : "—"}
                                            </div>
                                            {f.price_override != null && (
                                                <div className="text-[10px] text-[hsl(var(--primary))]">override</div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
